// Non-modal, persistent allergen selector (UX spec §1).
//
// MOBILE (<768px): a draggable bottom sheet with 2 snap points — a collapsed
// "peeked handle" above the timeline card, and a medium snap (<=55vh) that
// reveals the chip grid + legend. The handle row doubles as a summary of the
// current selection and a tap-to-toggle trigger.
//
// DESKTOP (>=768px): the SAME element is restyled by CSS into a floating side
// panel (handle hidden, content always visible); the JS drag/height logic is a
// no-op there (guarded by the mobile media query).
//
// Gesture isolation (spec §2): the handle owns `touch-action: none` +
// setPointerCapture so a vertical drag never reaches the map (pan) or the
// timeline (scrub); the scrollable content uses `touch-action: pan-y;
// overscroll-behavior: contain` so chip-grid scrolling never bubbles to the map.
//
// Collapse/expand animates the sheet's HEIGHT (not a translateY of a tall box):
// the sheet's bottom edge stays anchored just above the timeline, so the timeline
// is ALWAYS visible and the sheet never overhangs/covers it. (Deliberate
// deviation from the spec's translateY snippet, which would push the sheet body
// down over the timeline — see the report.)

const MOBILE_MQ = "(max-width: 767px)";
const EMPTY_COPY = "Which pollens affect you?";

export interface AllergenSheetOptions {
  /** The allergen chip grid element (AllergenPanel.el). */
  grid: HTMLElement;
  /** The legend element (Legend.el). */
  legend: HTMLElement;
  onSelectAll: () => void;
  onClear: () => void;
}

export class AllergenSheet {
  /** #allergen-sheet — caller mounts on the app root. */
  readonly el: HTMLElement;
  private handle: HTMLElement;
  private summary: HTMLElement;
  private content: HTMLElement;
  private expanded = false;

  constructor(opts: AllergenSheetOptions) {
    this.el = document.createElement("section");
    this.el.id = "allergen-sheet";
    this.el.setAttribute("role", "region");
    this.el.setAttribute("aria-label", "Pollen type selector");

    // --- Handle row (drag pill + summary + chevron) ---
    this.handle = document.createElement("div");
    this.handle.className = "sheet-handle";
    this.handle.setAttribute("role", "button");
    this.handle.tabIndex = 0;
    this.handle.setAttribute("aria-expanded", "false");
    this.handle.setAttribute("aria-controls", "allergen-grid");

    const grip = document.createElement("span");
    grip.className = "sheet-grip";
    grip.setAttribute("aria-hidden", "true");

    this.summary = document.createElement("span");
    this.summary.className = "sheet-summary";

    const chevron = document.createElement("span");
    chevron.className = "sheet-chevron";
    chevron.setAttribute("aria-hidden", "true");
    chevron.textContent = "›"; // ›

    this.handle.append(grip, this.summary, chevron);

    // --- Scrollable content (header + grid + legend) ---
    this.content = document.createElement("div");
    this.content.className = "sheet-content";

    const header = document.createElement("div");
    header.className = "sheet-header";

    const title = document.createElement("h2");
    title.className = "sheet-title";
    title.textContent = EMPTY_COPY;

    const subtext = document.createElement("p");
    subtext.className = "sheet-subtext";
    subtext.textContent =
      "Pick the types you react to — we’ll show you when and where " +
      "they peak across Europe, North America and East Asia.";

    const actions = document.createElement("div");
    actions.className = "panel-actions";
    const all = button("Select all", () => opts.onSelectAll());
    const clear = button("Clear", () => opts.onClear());
    actions.append(all, clear);

    header.append(title, subtext, actions);
    this.content.append(header, opts.grid, opts.legend);
    this.el.append(this.handle, this.content);

    this.setSummary([]);
    this.bindHandle();

    // Keep heights sane when the viewport changes / crosses the breakpoint.
    window.addEventListener("resize", () => this.onResize());
    this.applyCollapsedHeight();
  }

  // ---- public API ----

  get isExpanded(): boolean {
    return this.expanded;
  }

  expand(): void {
    this.expanded = true;
    this.handle.setAttribute("aria-expanded", "true");
    this.el.classList.add("expanded");
    if (this.isMobile()) {
      this.el.style.height = `${this.expandedHeight()}px`;
      const firstChip = this.el.querySelector<HTMLButtonElement>(".allergen-chip");
      firstChip?.focus({ preventScroll: true });
    }
  }

  collapse(): void {
    this.expanded = false;
    this.handle.setAttribute("aria-expanded", "false");
    this.el.classList.remove("expanded");
    if (this.isMobile()) this.applyCollapsedHeight();
  }

  toggle(): void {
    this.expanded ? this.collapse() : this.expand();
  }

  /** Update the collapsed-handle summary from the selected english names. */
  setSummary(names: string[]): void {
    if (names.length === 0) {
      this.summary.textContent = EMPTY_COPY;
      this.summary.classList.add("empty");
      return;
    }
    this.summary.classList.remove("empty");
    if (names.length <= 3) {
      this.summary.textContent = names.join(", ");
    } else {
      this.summary.textContent = `${names[0]}, ${names[1]} +${names.length - 2}`;
    }
  }

  // ---- internals ----

  private isMobile(): boolean {
    return window.matchMedia(MOBILE_MQ).matches;
  }

  private handleHeight(): number {
    return this.handle.offsetHeight || 44;
  }

  private expandedHeight(): number {
    const cap = window.innerHeight * 0.55;
    const natural = this.handleHeight() + this.content.scrollHeight + 2;
    return Math.min(cap, natural);
  }

  private applyCollapsedHeight(): void {
    if (this.isMobile()) this.el.style.height = `${this.handleHeight()}px`;
  }

  private onResize(): void {
    if (!this.isMobile()) {
      // Desktop: let CSS own the panel sizing (height:auto !important).
      this.el.style.height = "";
      this.el.style.transition = "";
      return;
    }
    if (this.expanded) this.el.style.height = `${this.expandedHeight()}px`;
    else this.applyCollapsedHeight();
  }

  private bindHandle(): void {
    // Keyboard: Enter / Space toggles.
    this.handle.addEventListener("keydown", (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        this.toggle();
      }
    });

    let startY = 0;
    let startH = 0;
    let dragging = false;
    let moved = false;

    this.handle.addEventListener("pointerdown", (e) => {
      if (!this.isMobile()) return;
      e.preventDefault();
      e.stopPropagation(); // never let the map claim a sheet drag
      dragging = true;
      moved = false;
      startY = e.clientY;
      startH = this.el.getBoundingClientRect().height;
      this.handle.setPointerCapture(e.pointerId);
      this.el.style.transition = "none"; // 1:1 during the drag
    });

    this.handle.addEventListener("pointermove", (e) => {
      if (!dragging || !this.handle.hasPointerCapture(e.pointerId)) return;
      const delta = e.clientY - startY; // down (+) shrinks the sheet
      if (Math.abs(delta) > 5) moved = true;
      const minH = this.handleHeight();
      const maxH = this.expandedHeight();
      const h = Math.max(minH, Math.min(maxH, startH - delta));
      this.el.style.height = `${h}px`;
    });

    const end = (e: PointerEvent) => {
      if (!dragging) return;
      dragging = false;
      if (this.handle.hasPointerCapture(e.pointerId)) {
        this.handle.releasePointerCapture(e.pointerId);
      }
      this.el.style.transition = "";
      if (!moved) {
        this.toggle();
        return;
      }
      // Snap: open if the sheet is past ~30% of the way to fully expanded.
      const minH = this.handleHeight();
      const maxH = this.expandedHeight();
      const h = this.el.getBoundingClientRect().height;
      if (h > minH + (maxH - minH) * 0.3) this.expand();
      else this.collapse();
    };
    this.handle.addEventListener("pointerup", end);
    this.handle.addEventListener("pointercancel", end);
  }
}

function button(label: string, onClick: () => void): HTMLButtonElement {
  const b = document.createElement("button");
  b.type = "button";
  b.className = "link-btn";
  b.textContent = label;
  b.addEventListener("click", onClick);
  return b;
}
